import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  ArrowRight,
  Wrench,
  Calendar,
  Package,
  BarChart3,
  Smartphone,
  Clock,
  Info,
  Play,
} from 'lucide-react';

// Replace these with your YouTube video IDs once uploaded
const CMMS_TEASER_VIDEO_ID      = 'CMMS_TEASER_VIDEO_ID';
const CMMS_WALKTHROUGH_VIDEO_ID = 'CMMS_WALKTHROUGH_VIDEO_ID';

const TIER_COLORS = [
  { bg: 'bg-slate-50', text: 'text-slate-800', sub: 'text-slate-500' },
  { bg: 'bg-primary-600', text: 'text-white', sub: 'text-primary-100' },
  { bg: 'bg-gray-800', text: 'text-white', sub: 'text-gray-300' },
];

const CMSSPage = () => {
  const { t } = useTranslation();
  const [billingCycle, setBillingCycle] = useState('annual');
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [plansError, setPlansError] = useState(false);

  const transformPlan = (plan) => ({
    name: plan.planName || plan.name || 'Plan',
    monthly: Number(plan.monthlyPrice) || 0,
    annual: Number(plan.annualPrice) || 0,
    description: plan.description || '',
    popular: plan.popular || false,
    disclaimer: t('pricing.extraSlots'),
    features: [
      plan.includedUsers != null ? `${plan.includedUsers} users` : null,
      plan.includedAssets != null ? `${plan.includedAssets} assets` : null,
      plan.includedLocations != null ? `${plan.includedLocations} locations` : null,
      plan.includedBranches != null ? `${plan.includedBranches} branches` : null,
      ...(Array.isArray(plan.features) ? plan.features : []),
    ].filter(Boolean),
  });

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await fetch('/api/subscriptions/cmms/plans');
        if (!res.ok) throw new Error('Failed to fetch plans');
        const data = await res.json();
        const arr = Array.isArray(data) ? data : (data.content ?? []);
        setPlans(arr.map(transformPlan));
      } catch (err) {
        console.error('Failed to fetch CMMS pricing plans:', err);
        setPlansError(true);
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  const features = [
    { icon: Wrench,      title: t('cmmsPage.f1Title'), description: t('cmmsPage.f1Desc') },
    { icon: Calendar,    title: t('cmmsPage.f2Title'), description: t('cmmsPage.f2Desc') },
    { icon: Package,     title: t('cmmsPage.f3Title'), description: t('cmmsPage.f3Desc') },
    { icon: BarChart3,   title: t('cmmsPage.f4Title'), description: t('cmmsPage.f4Desc') },
    { icon: Smartphone,  title: t('cmmsPage.f5Title'), description: t('cmmsPage.f5Desc') },
    { icon: Clock,       title: t('cmmsPage.f6Title'), description: t('cmmsPage.f6Desc') },
  ];

  const useCases = [
    {
      industry:  t('cmmsPage.uc1Industry'),
      challenge: t('cmmsPage.uc1Challenge'),
      solution:  t('cmmsPage.uc1Solution'),
      result:    t('cmmsPage.uc1Result'),
    },
    {
      industry:  t('cmmsPage.uc2Industry'),
      challenge: t('cmmsPage.uc2Challenge'),
      solution:  t('cmmsPage.uc2Solution'),
      result:    t('cmmsPage.uc2Result'),
    },
    {
      industry:  t('cmmsPage.uc3Industry'),
      challenge: t('cmmsPage.uc3Challenge'),
      solution:  t('cmmsPage.uc3Solution'),
      result:    t('cmmsPage.uc3Result'),
    },
    {
      industry:  t('cmmsPage.uc4Industry'),
      challenge: t('cmmsPage.uc4Challenge'),
      solution:  t('cmmsPage.uc4Solution'),
      result:    t('cmmsPage.uc4Result'),
    },
  ];

  return (
    <>
      <Helmet>
        <title>CMMS - Maintenance Management System | mwbTechs</title>
        <meta
          name="description"
          content="Comprehensive CMMS solution for asset tracking, work order management, and preventive maintenance. Reduce downtime and optimize operations."
        />
      </Helmet>

      <div className="pt-20 min-h-screen bg-white">
        {/* Hero Section */}
        <section className="section-sm bg-gradient-to-br from-primary-50 to-blue-50">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-full mb-6">
                  <Wrench className="w-4 h-4" />
                  <span className="font-medium">{t('cmmsPage.badge')}</span>
                </div>
                <h1 className="text-5xl font-bold text-gray-900 mb-6">
                  {t('cmmsPage.heroTitle')}
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  {t('cmmsPage.heroSubtitle')}
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/signup" className="btn btn-primary text-lg">
                    {t('cmmsPage.startFreeTrial')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                  <button
                    onClick={() => document.getElementById('cmms-video-section').scrollIntoView({ behavior: 'smooth' })}
                    className="btn btn-outline text-lg flex items-center gap-2"
                  >
                    <Play className="w-5 h-5" />
                    {t('cmmsPage.watchWalkthrough')}
                  </button>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200">
                  <div className="aspect-video rounded-xl overflow-hidden bg-gray-900">
                    <iframe
                      src={`https://www.youtube.com/embed/${CMMS_TEASER_VIDEO_ID}?autoplay=1&mute=1&loop=1&playlist=${CMMS_TEASER_VIDEO_ID}&controls=0&rel=0&modestbranding=1`}
                      title="mwbTechs CMMS — Quick Overview"
                      allow="autoplay; encrypted-media"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </div>
                  <div className="mt-6 grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-primary-600">35%</div>
                      <div className="text-sm text-gray-600">{t('cmmsPage.lessDowntime')}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-primary-600">60%</div>
                      <div className="text-sm text-gray-600">{t('cmmsPage.fasterResponse')}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-primary-600">$2M</div>
                      <div className="text-sm text-gray-600">{t('cmmsPage.avgSavings')}</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="section">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('cmmsPage.featuresTitle')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('cmmsPage.featuresSubtitle')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-lg transition-shadow"
                  >
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-primary-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className="section bg-gray-50">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('cmmsPage.useCasesTitle')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('cmmsPage.useCasesSubtitle')}
              </p>
            </div>

            <div className="space-y-6">
              {useCases.map((useCase, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-8 shadow-sm"
                >
                  <div className="flex flex-wrap items-start gap-4">
                    <div className="bg-primary-100 text-primary-700 px-4 py-2 rounded-full font-semibold">
                      {useCase.industry}
                    </div>
                    <div className="flex-1 min-w-[200px]">
                      <div className="grid md:grid-cols-3 gap-6">
                        <div>
                          <div className="text-sm text-gray-500 mb-1">{t('cmmsPage.challenge')}</div>
                          <div className="font-medium text-gray-900">{useCase.challenge}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500 mb-1">{t('cmmsPage.solution')}</div>
                          <div className="font-medium text-gray-900">{useCase.solution}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500 mb-1">{t('cmmsPage.result')}</div>
                          <div className="font-bold text-primary-600">{useCase.result}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Walkthrough Video Section */}
        <section id="cmms-video-section" className="section">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-full mb-6">
                <Play className="w-4 h-4" />
                <span className="font-medium">{t('cmmsPage.videoSectionBadge')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('cmmsPage.videoSectionTitle')}
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                {t('cmmsPage.videoSectionSubtitle')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="max-w-4xl mx-auto"
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200 bg-gray-900 aspect-video">
                <iframe
                  src={`https://www.youtube.com/embed/${CMMS_WALKTHROUGH_VIDEO_ID}?rel=0&modestbranding=1`}
                  title="mwbTechs CMMS — Full Walkthrough"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
              <div className="text-center mt-8">
                <Link to="/signup" className="btn btn-primary text-lg">
                  {t('cmmsPage.startFreeTrial')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="section bg-gray-50">
          <div className="container-custom">
            <div className="text-center mb-10">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('cmmsPage.pricingTitle')}
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                {t('cmmsPage.pricingSubtitle')}
              </p>
              {/* Billing cycle toggle */}
              <div className="inline-flex items-center bg-gray-100 rounded-full p-1">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-6 py-2 rounded-full font-medium transition-all ${
                    billingCycle === 'monthly'
                      ? 'bg-white text-gray-900 shadow'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('cmmsPage.monthly')}
                </button>
                <button
                  onClick={() => setBillingCycle('annual')}
                  className={`px-6 py-2 rounded-full font-medium transition-all ${
                    billingCycle === 'annual'
                      ? 'bg-white text-gray-900 shadow'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('cmmsPage.annual')}
                  <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                    {t('cmmsPage.save10')}
                  </span>
                </button>
              </div>
            </div>

            {loadingPlans ? (
              <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {[1, 2, 3].map(i => (
                  <div key={i} className="pricing-card animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-1/2 mb-4" />
                    <div className="h-10 bg-gray-200 rounded w-3/4 mb-6" />
                    <div className="space-y-3">
                      {[1, 2, 3, 4, 5].map(j => (
                        <div key={j} className="h-4 bg-gray-100 rounded w-full" />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : plansError ? (
              <div className="text-center py-16">
                <p className="text-gray-500 text-lg">{t('cmmsPage.contactPricing')}</p>
                <Link to="/contact" className="text-primary-600 underline mt-2 inline-block">
                  {t('cmmsPage.getInTouch')}
                </Link>
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {plans.map((plan, index) => {
                  const colors = TIER_COLORS[index] ?? TIER_COLORS[0];
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className={`pricing-card overflow-hidden ${index === 1 ? 'pricing-card-featured' : ''}`}
                    >
                      {/* Colored header strip */}
                      <div className={`-mx-8 -mt-8 px-8 pt-8 pb-5 text-center mb-6 ${colors.bg}`}>
                        {index === 1 && (
                          <span className="inline-block text-xs font-semibold bg-white/25 text-white px-3 py-1 rounded-full mb-3 uppercase tracking-wider">
                            {t('cmmsPage.mostPopular')}
                          </span>
                        )}
                        <h3 className={`text-2xl font-bold ${colors.text}`}>{plan.name}</h3>
                        {plan.description && (
                          <p className={`text-sm mt-1 ${colors.sub}`}>{plan.description}</p>
                        )}
                      </div>

                      {/* Price */}
                      <div className="text-center mb-6">
                        {plan.monthly === 0 ? (
                          <div className="flex items-baseline justify-center gap-2">
                            <span className="text-4xl font-bold text-gray-900">{t('cmmsPage.custom')}</span>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-baseline justify-center gap-2">
                              <span className="text-5xl font-bold text-gray-900">
                                ${billingCycle === 'monthly' ? plan.monthly : Math.round(plan.annual / 12)}
                              </span>
                              <span className="text-gray-600">{t('cmmsPage.perMonth')}</span>
                            </div>
                            {billingCycle === 'annual' && (
                              <p className="text-sm text-green-600 mt-1">${plan.annual} {t('cmmsPage.billedAnnually')}</p>
                            )}
                          </>
                        )}
                      </div>

                      <ul className="space-y-3 mb-4">
                        {plan.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {plan.disclaimer && (
                        <p className="flex items-start gap-1.5 text-xs text-gray-500 italic mb-6">
                          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-400" />
                          {plan.disclaimer}
                        </p>
                      )}

                      <Link
                        to="/signup"
                        className={`w-full btn ${index === 1 ? 'btn-primary' : 'btn-outline'}`}
                      >
                        {t('cmmsPage.getStarted')}
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-sm bg-gradient-primary text-white">
          <div className="container-custom text-center">
            <h2 className="text-4xl font-bold mb-4">
              {t('cmmsPage.ctaTitle')}
            </h2>
            <p className="text-xl text-white opacity-90 mb-8">
              {t('cmmsPage.ctaSubtitle')}
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/signup" className="btn btn-white text-lg">
                {t('cmmsPage.startFreeTrial')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <button
                onClick={() => document.getElementById('cmms-video-section').scrollIntoView({ behavior: 'smooth' })}
                className="btn btn-outline text-white border-white hover:bg-white hover:text-primary-600 text-lg flex items-center gap-2"
              >
                <Play className="w-5 h-5" />
                {t('cmmsPage.watchWalkthrough')}
              </button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CMSSPage;
